import React, { useState } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';

const AgentSelector = ({ onClose, onRunAnalysis }) => {
  // List of available agents with descriptions
  const agents = [
    {
      id: 'SFEE',
      name: 'SFEE Agent',
      icon: '🔍',
    },
    {
      id: 'EFEX',
      name: 'EFEX Agent',
      icon: '📊',
    },
    {
      id: 'EOF',
      name: 'EOF Agent',
      icon: '📝',
    },
    {
      id: 'Galenica',
      name: 'Galenica Agent',
      icon: '⚕️',
    },
    {
      id: 'Citation',
      name: 'Citation Agent',
      icon: '📋',
    },
    {
      id: 'Grammar',
      name: 'Grammar Agent',
      icon: '📚',
    }
  ];
  
  // Initialize with all agents selected by default
  const [selectedAgents, setSelectedAgents] = useState(agents.map(agent => agent.id));
  const [error, setError] = useState('');
  
  // Toggle agent selection
  const toggleAgent = (agentId) => {
    if (selectedAgents.includes(agentId)) {
      setSelectedAgents(selectedAgents.filter(id => id !== agentId));
    } else {
      setSelectedAgents([...selectedAgents, agentId]);
    }
    
    // Clear error when user makes a selection
    setError('');
  };
  
  // Handle run analysis action
  const handleRunAnalysis = () => {
    if (selectedAgents.length === 0) {
      setError('Please select at least one agent for analysis');
      return;
    }
    
    onRunAnalysis(selectedAgents);
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-dark rounded-xl shadow-xl max-w-lg w-full mx-auto overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-dark-lighter p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Select Analysis Agents</h2>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-dark-darker text-gray-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Agent options */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg flex items-center text-red-400">
              <AlertTriangle size={16} className="mr-2" />
              {error}
            </div>
          )}
          
          <p className="text-gray-400 mb-4">
            All agents are selected by default. Deselect any agents you don't want to include in the analysis. At least one agent must remain selected.
          </p>
          
          <div className="space-y-3">
            {agents.map((agent) => (
              <div 
                key={agent.id}
                onClick={() => toggleAgent(agent.id)}
                className={`p-4 rounded-lg cursor-pointer transition-all duration-200 flex ${
                  selectedAgents.includes(agent.id)
                    ? 'bg-primary/20 border border-primary'
                    : 'bg-dark-lighter border border-dark-lighter hover:border-gray-600'
                }`}
              >
                <div className="mr-3 text-2xl">{agent.icon}</div>
                <div className="flex-grow">
                  <h3 className="font-medium">{agent.name}</h3>
                  <p className="text-sm text-gray-400">{agent.description}</p>
                </div>
                <div className="self-center ml-3">
                  <div className={`w-6 h-6 rounded-full border ${
                    selectedAgents.includes(agent.id)
                      ? 'border-primary bg-primary flex items-center justify-center'
                      : 'border-gray-500'
                  }`}>
                    {selectedAgents.includes(agent.id) && (
                      <Check size={14} className="text-white" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer/Actions */}
        <div className="border-t border-dark-lighter p-4 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-dark-lighter"
          >
            Cancel
          </button>
          <button
            onClick={handleRunAnalysis}
            className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white font-medium"
          >
            Run Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentSelector;